import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    getHarmonyRequests,
    acceptHarmonyRequest,
    rejectHarmonyRequest,
    cancelHarmonyRequest,
    listMyHarmonies
} from '../../client';
import { getCurrentUsername, isLoggedIn } from '../../auth/auth';

const toOverallScore = (h) => {
    const chemistry = Number(h.chemistry_score || 0);
    const friction = Number(h.friction_score || 0);
    const direction = Number(h.direction_score || 0);
    const frictionSafe = 100 - friction;
    return Math.round((chemistry * 0.35) + (frictionSafe * 0.3) + (direction * 0.35));
};

const scoreLabel = (score) => {
    if (score >= 80) return '매우 좋음';
    if (score >= 65) return '좋음';
    if (score >= 50) return '보통';
    if (score >= 35) return '주의';
    return '조율 필요';
};

const HarmonyLobby = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [receivedRequests, setReceivedRequests] = useState([]);
    const [sentRequests, setSentRequests] = useState([]);
    const [myHarmonies, setMyHarmonies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('match'); // 'match', 'requests', 'my'
    const [isAuth, setIsAuth] = useState(false);
    const [myUsername, setMyUsername] = useState('');
    const [manualMyId, setManualMyId] = useState('');

    useEffect(() => {
        const loggedIn = isLoggedIn();
        setIsAuth(loggedIn);
        if (loggedIn) {
            setMyUsername(getCurrentUsername());
            fetchData();
        }
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const reqs = await getHarmonyRequests();
            setReceivedRequests(reqs.received || []);
            setSentRequests(reqs.sent || []);
            const harmonies = await listMyHarmonies();
            const normalized = (harmonies || [])
                .map((h) => ({ ...h, overall_score: toOverallScore(h) }))
                .sort((a, b) => b.overall_score - a.overall_score);
            setMyHarmonies(normalized);
        } catch (e) {
            console.error("Fetch failed", e);
        } finally {
            setLoading(false);
        }
    };

    const handleQuickCompare = () => {
        const targetId = searchQuery.trim();
        if (!targetId) {
            alert('상대 아이디를 입력해주세요.');
            return;
        }

        const myId = (myUsername || getCurrentUsername() || manualMyId).trim();
        if (!myId) {
            alert('내 아이디를 확인할 수 없습니다. 내 아이디를 직접 입력해주세요.');
            return;
        }

        if (targetId === myId) {
            alert('본인 아이디는 입력할 수 없습니다.');
            return;
        }

        navigate(`/harmony/result?id1=${encodeURIComponent(myId)}&id2=${encodeURIComponent(targetId)}`);
    };

    const handleAccept = async (rid) => {
        try {
            await acceptHarmonyRequest(rid);
            alert('궁합이 생성되었습니다!');
            await fetchData();
            setActiveTab('my');
        } catch (e) {
            alert(e.message);
        }
    };

    return (
        <div className="lobby-container">
            <div className="lobby-content">
                <header className="lobby-header">
                    <button className="back-btn" onClick={() => navigate('/')}>&larr; 메인으로</button>
                    <h1>궁합 시스템</h1>
                    <p>친구와의 연애 궁합을 점수 + 행동 가이드로 확인해보세요</p>
                </header>

                {!isAuth ? (
                    <div className="auth-required-box">
                        <h2>로그인이 필요합니다 🔒</h2>
                        <p>우주적 궁합을 확인하려면 로그인이 필요합니다. 지금 바로 가입하거나 로그인하세요!</p>
                        <button className="login-btn" onClick={() => navigate('/login?redirect=/harmony/lobby')}>
                            로그인 하러 가기
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="lobby-tabs">
                            <button className={activeTab === 'match' ? 'active' : ''} onClick={() => setActiveTab('match')}>궁합 만들기</button>
                            <button className={activeTab === 'requests' ? 'active' : ''} onClick={() => setActiveTab('requests')}>
                                요청함 {receivedRequests.length > 0 && <span className="badge">{receivedRequests.length}</span>}
                            </button>
                            <button className={activeTab === 'my' ? 'active' : ''} onClick={() => setActiveTab('my')}>내 궁합</button>
                            <div className={`tab-indicator ${activeTab}`} />
                        </div>

                        <div className="tab-body">
                            <AnimatePresence mode="wait">
                                {activeTab === 'match' && (
                                    <motion.div key="match" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                                        <div className="search-box">
                                            <input
                                                type="text"
                                                placeholder="상대 아이디를 입력하세요"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleQuickCompare()}
                                            />
                                            <button onClick={handleQuickCompare} disabled={loading}>바로 궁합 보기</button>
                                        </div>
                                        <div className="quick-note">
                                            <span>내 기준 아이디: <strong>@{myUsername || '확인중'}</strong></span>
                                            <p>상대 아이디만 입력하면 바로 결과가 생성됩니다.</p>
                                            {!myUsername && (
                                                <div className="myid-input-wrap">
                                                    <input
                                                        type="text"
                                                        placeholder="내 아이디 직접 입력 (예: min9808)"
                                                        value={manualMyId}
                                                        onChange={(e) => setManualMyId(e.target.value)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}

                                {activeTab === 'requests' && (
                                    <motion.div key="requests" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                                        <section className="req-section">
                                            <h3>받은 요청</h3>
                                            {receivedRequests.map(req => (
                                                <div key={req.id} className="request-card">
                                                    <span className="from">@{req.from_user_id}</span>
                                                    <div className="actions">
                                                        <button className="accept" onClick={() => handleAccept(req.id)}>수락</button>
                                                        <button className="reject" onClick={() => rejectHarmonyRequest(req.id).then(fetchData)}>거절</button>
                                                    </div>
                                                </div>
                                            ))}
                                            {receivedRequests.length === 0 && <p className="empty-msg">받은 요청이 없습니다.</p>}
                                        </section>

                                        <section className="req-section">
                                            <h3>보낸 요청</h3>
                                            {sentRequests.map(req => (
                                                <div key={req.id} className="request-card">
                                                    <span className="to">@{req.to_user_id}</span>
                                                    <button className="cancel" onClick={() => cancelHarmonyRequest(req.id).then(fetchData)}>취소</button>
                                                </div>
                                            ))}
                                            {sentRequests.length === 0 && <p className="empty-msg">보낸 요청이 없습니다.</p>}
                                        </section>
                                    </motion.div>
                                )}

                                {activeTab === 'my' && (
                                    <motion.div key="my" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                                        <div className="harmonies-list">
                                            {myHarmonies.map(h => (
                                                <div key={h.id} className="harmony-item" onClick={() => navigate(`/harmony/collaborative/${h.id}`)}>
                                                    <div className="h-info">
                                                        <span className="partner">@{h.other_user_id}</span>
                                                        <div className="scores-mini">
                                                            <span>초반 {h.chemistry_score}</span>
                                                            <span>갈등 {h.friction_score}</span>
                                                            <span>장기 {h.direction_score}</span>
                                                        </div>
                                                        <div className="overall-row">
                                                            <span className="overall-score">종합 {h.overall_score}%</span>
                                                            <span className="overall-label">{scoreLabel(h.overall_score)}</span>
                                                        </div>
                                                    </div>
                                                    <span className="arrow">→</span>
                                                </div>
                                            ))}
                                            {myHarmonies.length === 0 && <p className="empty-msg">아직 생성된 궁합이 없습니다.</p>}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </>
                )}
            </div>

            <style jsx>{`
                .lobby-container {
                    min-height: 100vh;
                    background: radial-gradient(circle at top left, #1e1b4b, #0f172a);
                    color: white;
                    padding: 6rem 2rem 4rem;
                    display: flex;
                    justify-content: center;
                }
                .lobby-content {
                    max-width: 600px;
                    width: 100%;
                }
                .lobby-header { text-align: center; margin-bottom: 3rem; position: relative; }
                .back-btn {
                    position: absolute;
                    left: 0;
                    top: 0;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    color: #cbd5e1;
                    padding: 0.5rem 1rem;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: 0.2s;
                }
                .back-btn:hover { background: rgba(255, 255, 255, 0.1); color: white; }
                .lobby-header h1 { font-size: 2.5rem; margin-bottom: 0.5rem; background: linear-gradient(to right, #f472b6, #8b5cf6); -webkit-background-clip: text; -webkit-text-fill-color: transparent; padding-top: 1rem; }
                .lobby-header p { color: #94a3b8; }

                .auth-required-box {
                    text-align: center;
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    padding: 4rem 2rem;
                    border-radius: 24px;
                }
                .auth-required-box h2 { font-size: 1.8rem; margin-bottom: 1rem; color: #f472b6; }
                .auth-required-box p { color: #cbd5e1; margin-bottom: 2rem; line-height: 1.6; }
                .login-btn {
                    background: linear-gradient(135deg, #ec4899, #8b5cf6);
                    border: none;
                    padding: 1rem 2rem;
                    border-radius: 16px;
                    color: white;
                    font-weight: 700;
                    font-size: 1.1rem;
                    cursor: pointer;
                    transition: transform 0.2s;
                }
                .login-btn:hover { transform: translateY(-2px); }

                .lobby-tabs {
                    display: flex;
                    gap: 1rem;
                    margin-bottom: 2rem;
                    position: relative;
                    border-bottom: 1px solid rgba(255,255,255,0.1);
                    padding-bottom: 0.5rem;
                }
                .lobby-tabs button {
                    background: none;
                    border: none;
                    color: #94a3b8;
                    font-size: 1.1rem;
                    font-weight: 600;
                    padding: 0.5rem 1rem;
                    cursor: pointer;
                    position: relative;
                    transition: color 0.3s;
                }
                .lobby-tabs button.active { color: white; }
                .badge { background: #ef4444; color: white; font-size: 0.7rem; padding: 0.1rem 0.4rem; border-radius: 99px; vertical-align: top; margin-left: 0.2rem; }
                
                .tab-indicator {
                    position: absolute;
                    bottom: -1px;
                    height: 2px;
                    background: #ec4899;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .tab-indicator.match { left: 0; width: 100px; }
                .tab-indicator.requests { left: 116px; width: 100px; }
                .tab-indicator.my { left: 232px; width: 80px; }

                .search-box { display: flex; gap: 0.5rem; margin-bottom: 2rem; }
                .search-box input { flex: 1; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 0.8rem 1rem; color: white; outline: none; }
                .search-box button { background: #8b5cf6; border: none; border-radius: 12px; padding: 0.8rem 1.1rem; color: white; cursor: pointer; font-weight: 700; white-space: nowrap; }
                .search-box button:disabled { opacity: 0.6; cursor: not-allowed; }
                .quick-note {
                    background: rgba(147, 197, 253, 0.1);
                    border: 1px solid rgba(147, 197, 253, 0.3);
                    border-radius: 12px;
                    padding: 0.9rem 1rem;
                    margin-top: 0.9rem;
                }
                .quick-note span {
                    display: block;
                    color: #dbeafe;
                    font-size: 0.86rem;
                    font-weight: 700;
                }
                .quick-note p {
                    margin: 0.25rem 0 0;
                    color: #bfdbfe;
                    font-size: 0.82rem;
                }
                .myid-input-wrap {
                    margin-top: 0.6rem;
                }
                .myid-input-wrap input {
                    width: 100%;
                    background: rgba(15, 23, 42, 0.55);
                    border: 1px solid rgba(147, 197, 253, 0.35);
                    border-radius: 9px;
                    color: #f8fafc;
                    padding: 0.55rem 0.65rem;
                    font-size: 0.84rem;
                    box-sizing: border-box;
                }

                .user-card, .request-card, .harmony-item {
                    background: rgba(255,255,255,0.03);
                    border: 1px solid rgba(255,255,255,0.05);
                    border-radius: 16px;
                    padding: 1.2rem;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 1rem;
                    transition: all 0.2s;
                }
                .harmony-item { cursor: pointer; }
                .harmony-item:hover { background: rgba(255,255,255,0.06); transform: translateX(5px); }
                
                .username, .partner { font-weight: 700; color: #e2e8f0; }
                .scores-mini { display: flex; gap: 0.7rem; font-size: 0.86rem; color: #94a3b8; margin-top: 0.25rem; flex-wrap: wrap; }
                .overall-row { display: flex; gap: 0.5rem; margin-top: 0.45rem; align-items: center; }
                .overall-score {
                    font-size: 0.82rem;
                    font-weight: 700;
                    color: #dbeafe;
                    background: rgba(59, 130, 246, 0.18);
                    border: 1px solid rgba(147, 197, 253, 0.35);
                    border-radius: 999px;
                    padding: 0.16rem 0.5rem;
                }
                .overall-label {
                    font-size: 0.8rem;
                    color: #cbd5e1;
                    font-weight: 600;
                }
                .request-btn, .accept { background: #ec4899; border: none; padding: 0.5rem 1rem; border-radius: 8px; color: white; cursor: pointer; }
                .reject, .cancel { background: rgba(255,255,255,0.1); border: none; padding: 0.5rem 1rem; border-radius: 8px; color: white; cursor: pointer; margin-left: 0.5rem; }
                
                .req-section { margin-bottom: 3rem; }
                .req-section h3 { font-size: 1rem; color: #94a3b8; margin-bottom: 1rem; text-transform: uppercase; letter-spacing: 1px; }
                .empty-msg { text-align: center; color: #4b5563; padding: 2rem; }

                @media (max-width: 600px) {
                    .back-btn { position: relative; margin-bottom: 1rem; display: inline-block; }
                    .lobby-header h1 { padding-top: 0; }
                }
            `}</style>
        </div>
    );
};

export default HarmonyLobby;
